import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Package, Calendar, MessageSquare, 
  Users, Settings, LogOut, Edit2, Trash2, Plus, 
  Check, X, Clock, Euro, Eye, RefreshCw, FileText, Image, Tag, Gift, Copy, Bell,
  ChevronLeft, ChevronRight, Mail, Phone, User, History, Ban, Lock, Unlock,
  Ticket, IdCard, UserPlus, HelpCircle, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AdminLogin } from '../components/admin/AdminLogin';
import { DashboardTab } from '../components/admin/DashboardTab';
import { BonosTab } from '../components/admin/BonosTab';
import { CustomerCardsTab } from '../components/admin/CustomerCardsTab';
import { InvoicesTab } from '../components/admin/InvoicesTab';
import { WalkInTab } from '../components/admin/WalkInTab';
import { SignaturesTab } from '../components/admin/SignaturesTab';
import { InternalPromosTab } from '../components/admin/InternalPromosTab';
import { HelpTab } from '../components/admin/HelpTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState('admin');
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [content, setContent] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [isCreateServiceOpen, setIsCreateServiceOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [isPromoDialogOpen, setIsPromoDialogOpen] = useState(false);
  const [remindersStatus, setRemindersStatus] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Calendar states
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarBookings, setCalendarBookings] = useState({});
  const [isManualBookingOpen, setIsManualBookingOpen] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [manualBooking, setManualBooking] = useState({
    service_id: '',
    date: '',
    time: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    num_adults: 1,
    num_children: 0,
    num_babies: 0,
    booking_type: 'solo_adultos',
    notes: '',
    bono_code: '',
    room: 'sala_grande',
    duration_minutes: 60,
    status: 'confirmed',
    discount_promo_id: '',
    manual_price: '',
    discount_reason: ''
  });
  const [bonoSearchResults, setBonoSearchResults] = useState([]);
  const [bonoSearchOpen, setBonoSearchOpen] = useState(false);
  const [internalPromos, setInternalPromos] = useState([]);
  
  // Customers states
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Booking edit states
  const [isEditBookingOpen, setIsEditBookingOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [isBookingDetailOpen, setIsBookingDetailOpen] = useState(false);
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);
  const [isDayDetailOpen, setIsDayDetailOpen] = useState(false);
  const [selectedDayBookings, setSelectedDayBookings] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  
  // Blocked dates states
  const [blockedDates, setBlockedDates] = useState([]);
  const [isBlockRangeOpen, setIsBlockRangeOpen] = useState(false);
  const [blockRange, setBlockRange] = useState({ start_date: '', end_date: '', reason: 'Centro cerrado' });
  const [blockingRange, setBlockingRange] = useState(false);

  // Room filter for the calendar view
  const [roomFilter, setRoomFilter] = useState('all'); // 'all' | 'sala_grande' | 'sala_pequena'
  const filterByRoom = (list) => {
    if (roomFilter === 'all') return list;
    return list.filter((b) => (b.room || 'sala_grande') === roomFilter);
  };
  
  // Google Calendar states
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [showManualCodeEntry, setShowManualCodeEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  
  // Note: Auth state is kept in-memory only (never persisted to localStorage/sessionStorage)
  // to avoid XSS token exfiltration. OAuth uses a popup flow so no redirect-survival is needed.
  
  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    discount_type: 'percent',
    discount_percent: 10,
    discount_amount: 5,
    description: '',
    max_uses: '',
    valid_until: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    
    try {
      const response = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setAuthHeader(`Basic ${auth}`);
      setIsAuthenticated(true);
      setUserRole(response.data.role || 'admin');
      setUserPermissions(response.data.permissions || []);
      // Set default tab based on role
      if (response.data.role === 'office') {
        setActiveTab('bookings');
      }
      toast.success('Bienvenido al panel de administración');
    } catch (error) {
      toast.error('Credenciales incorrectas');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setCredentials({ username: '', password: '' });
    setUserRole('admin');
    setUserPermissions([]);
    setActiveTab('dashboard');
  };

  // Helper to check if user has permission
  const hasPermission = (permission) => {
    if (userPermissions.includes('all')) return true;
    return userPermissions.includes(permission);
  };

  // Filter tabs based on role
  const getAvailableTabs = () => {
    const allTabs = [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'all' },
      { id: 'calendar', icon: Calendar, label: 'Calendario', permission: 'bookings' },
      { id: 'bonos', icon: Ticket, label: 'Bonos', permission: 'bookings' },
      { id: 'customer-cards', icon: IdCard, label: 'Fichas clientes', permission: 'bookings' },
      { id: 'invoices', icon: FileText, label: 'Facturas', permission: 'bookings' },
      { id: 'walk-in', icon: UserPlus, label: 'Alta presencial', permission: 'bookings' },
      { id: 'signatures', icon: ShieldCheck, label: 'Firmas', permission: 'bookings' },
      { id: 'internal-promos', icon: Tag, label: 'Promos internas', permission: 'bookings' },
      { id: 'content', icon: FileText, label: 'Contenido', permission: 'all' },
      { id: 'services', icon: Package, label: 'Servicios', permission: 'all' },
      { id: 'bookings', icon: Clock, label: 'Reservas', permission: 'bookings' },
      { id: 'customers', icon: Users, label: 'Clientes (legacy)', permission: 'bookings' },
      { id: 'promos', icon: Tag, label: 'Códigos Promo', permission: 'all' },
      { id: 'contacts', icon: MessageSquare, label: 'Mensajes', permission: 'contacts' },
      { id: 'help', icon: HelpCircle, label: 'Ayuda', permission: 'all' },
    ];
    
    if (userRole === 'office') {
      // Office tiene acceso a todo EXCEPTO contenido (CMS)
      return allTabs.filter(tab => tab.id !== 'content');
    }
    return allTabs;
  };

  const fetchData = async () => {
    if (!authHeader) return;
    setLoading(true);
    
    try {
      const headers = { Authorization: authHeader };
      
      const [statsRes, servicesRes, bookingsRes, contactsRes, contentRes, promoRes, internalPromosRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/services`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/content`, { headers }),
        axios.get(`${API}/admin/promo-codes`, { headers }),
        axios.get(`${API}/admin/internal-promos`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setServices(servicesRes.data);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setContent(contentRes.data);
      setPromoCodes(promoRes.data);
      setInternalPromos(Array.isArray(internalPromosRes.data) ? internalPromosRes.data : []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchCalendarStatus();
    }
  }, [isAuthenticated]);

  // OAuth uses a popup (see connectGoogleCalendar), so no URL-param cleanup is needed.

  const fetchCalendarStatus = async () => {
    if (!authHeader) return;
    try {
      const response = await axios.get(`${API}/admin/calendar/status`, {
        headers: { Authorization: authHeader }
      });
      setCalendarConnected(response.data.connected);
      setCalendarEmail(response.data.email || '');
    } catch (error) {
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`, {
        headers: { Authorization: authHeader }
      });
      const authUrl = response.data.authorization_url;
      
      // Open Google auth in a popup window
      const popup = window.open(authUrl, 'google_calendar_auth', 'width=600,height=700,left=200,top=100');
      
      // Listen for message from the popup callback
      const handleMessage = async (event) => {
        if (event.data?.type === 'calendar_connected') {
          window.removeEventListener('message', handleMessage);
          toast.success(`Google Calendar conectado: ${event.data.email}`);
          setCalendarConnected(true);
          setCalendarEmail(event.data.email);
        }
        if (event.data?.type === 'calendar_error') {
          window.removeEventListener('message', handleMessage);
          toast.error(`Error: ${event.data.error}`);
          setShowManualCodeEntry(true);
        }
      };
      window.addEventListener('message', handleMessage);
      
      // Fallback: check if popup closes without message
      const checkClosed = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          // Check if calendar was connected
          setTimeout(() => fetchCalendarStatus(), 1000);
        }
      }, 1500);
      
    } catch (error) {
      toast.error('Error al iniciar conexión con Google Calendar');
      setShowManualCodeEntry(true);
    }
  };

  const connectWithManualCode = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    setConnectingCalendar(true);
    try {
      const response = await axios.post(
        `${API}/admin/calendar/connect-with-code?code=${encodeURIComponent(manualCode.trim())}`,
        null,
        { headers: { Authorization: authHeader } }
      );
      if (response.data.connected) {
        toast.success(`Google Calendar conectado: ${response.data.email}`);
        setCalendarConnected(true);
        setCalendarEmail(response.data.email);
        setShowManualCodeEntry(false);
        setManualCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al conectar. Verifica el código.');
    } finally {
      setConnectingCalendar(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('¿Desconectar Google Calendar?')) return;
    try {
      await axios.delete(`${API}/admin/calendar/disconnect`, {
        headers: { Authorization: authHeader }
      });
      setCalendarConnected(false);
      setCalendarEmail('');
      toast.success('Google Calendar desconectado');
    } catch (error) {
      toast.error('Error al desconectar');
    }
  };

  const syncCalendar = async () => {
    setSyncingCalendar(true);
    try {
      const response = await axios.post(`${API}/admin/calendar/sync`, null, {
        headers: { Authorization: authHeader }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncingCalendar(false);
    }
  };

  const updateService = async (serviceId, data) => {
    try {
      await axios.put(`${API}/admin/services/${serviceId}`, data, {
        headers: { Authorization: authHeader }
      });
      toast.success('Servicio actualizado');
      fetchData();
      setIsDialogOpen(false);
      setEditingService(null);
    } catch (error) {
      toast.error('Error al actualizar servicio');
    }
  };

  const deleteService = async (serviceId, serviceName) => {
    if (!window.confirm(`¿Eliminar el servicio "${serviceName}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success('Servicio eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar servicio');
    }
  };

  const createService = async (data) => {
    try {
      const slug = data.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const serviceData = {
        id: slug,
        slug: slug,
        ...data,
        is_active: true
      };
      await axios.post(`${API}/admin/services`, serviceData, {
        headers: { Authorization: authHeader }
      });
      toast.success('Servicio creado');
      fetchData();
      setIsCreateServiceOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear servicio');
    }
  };

  const updateContent = async (contentId, data) => {
    try {
      await axios.put(`${API}/admin/content/${contentId}`, data, {
        headers: { Authorization: authHeader }
      });
      toast.success('Contenido actualizado');
      fetchData();
      setIsContentDialogOpen(false);
      setEditingContent(null);
    } catch (error) {
      toast.error('Error al actualizar contenido');
    }
  };

  // Promo codes functions
  const generateRandomCode = (prefix = 'HALO') => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const createPromoCode = async (e) => {
    e.preventDefault();
    if (!newPromoCode.code) {
      toast.error('El código es obligatorio');
      return;
    }
    const dtype = newPromoCode.discount_type === 'fixed' ? 'fixed' : 'percent';
    const percent = parseFloat(newPromoCode.discount_percent);
    const amount = parseFloat(newPromoCode.discount_amount);
    if (dtype === 'percent' && (!(percent > 0) || percent > 100)) {
      toast.error('Porcentaje inválido (1-100)');
      return;
    }
    if (dtype === 'fixed' && !(amount > 0)) {
      toast.error('Importe fijo debe ser mayor que 0');
      return;
    }
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: newPromoCode.code,
        discount_type: dtype,
        discount_percent: dtype === 'percent' ? percent : 0,
        discount_amount: dtype === 'fixed' ? amount : 0,
        description: newPromoCode.description,
        max_uses: newPromoCode.max_uses ? parseInt(newPromoCode.max_uses) : null,
        valid_until: newPromoCode.valid_until || null,
      }, {
        headers: { Authorization: authHeader }
      });
      toast.success('Código creado correctamente');
      fetchData();
      setIsPromoDialogOpen(false);
      setNewPromoCode({
        code: '',
        discount_type: 'percent',
        discount_percent: 10,
        discount_amount: 5,
        description: '',
        max_uses: '',
        valid_until: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear código');
    }
  };

  const deletePromoCode = async (codeId) => {
    if (!window.confirm('¿Eliminar este código?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${codeId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success('Código eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar código');
    }
  };

  const togglePromoCode = async (codeId, isActive) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${codeId}?is_active=${!isActive}`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success(isActive ? 'Código desactivado' : 'Código activado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar código');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado');
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}?status=${status}`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success('Reserva actualizada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar reserva');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success('Mensaje eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Booking action functions
  const deleteBooking = async (bookingId) => {
    // Look up booking from current state to know if it's linked to a bono
    const linkedBooking = bookings.find((b) => b.id === bookingId);
    const hasBono = linkedBooking?.bono_id;

    if (!window.confirm('¿Estás seguro de eliminar esta reserva? Esta acción no se puede deshacer.')) return;

    let restoreSession = false;
    if (hasBono) {
      restoreSession = window.confirm(
        `Esta reserva está asociada al bono ${linkedBooking.bono_code}. ¿Quieres devolver 1 sesión al bono?\n\nAceptar = Devolver sesión\nCancelar = Eliminar sin devolver`,
      );
    }

    try {
      await axios.delete(
        `${API}/admin/bookings/${bookingId}${restoreSession ? '?restore_bono_session=true' : ''}`,
        { headers: { Authorization: authHeader } }
      );
      toast.success(
        restoreSession
          ? `Reserva eliminada y sesión devuelta al bono`
          : 'Reserva eliminada'
      );
      fetchData();
      fetchCalendarBookings(calendarDate);
    } catch (error) {
      toast.error('Error al eliminar reserva');
    }
  };

  const resendConfirmationEmail = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/resend-confirmation`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success('Email de confirmación reenviado');
    } catch (error) {
      toast.error('Error al reenviar email');
    }
  };

  const downloadInvoice = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/invoice`, {
        headers: { Authorization: authHeader },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura_halo_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Factura descargada');
    } catch (error) {
      toast.error('Error al descargar factura');
    }
  };

  // Reminders functions
  const fetchRemindersStatus = async () => {
    if (!authHeader) return;
    try {
      const response = await axios.get(`${API}/admin/reminders-status`, {
        headers: { Authorization: authHeader }
      });
      setRemindersStatus(response.data);
    } catch (error) {
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success(`${response.data.reminders_sent} recordatorios enviados`);
      fetchRemindersStatus();
    } catch (error) {
      toast.error('Error al enviar recordatorios');
    } finally {
      setSendingReminders(false);
    }
  };

  // Calendar functions
  const fetchCalendarBookings = async (date) => {
    if (!authHeader) return;
    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = new Date(year, month, 1).toISOString().split('T')[0];
    const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
    
    try {
      const [bookingsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/bookings/calendar?start_date=${startDate}&end_date=${endDate}`, {
          headers: { Authorization: authHeader }
        }),
        axios.get(`${API}/admin/blocked-dates`, {
          headers: { Authorization: authHeader }
        })
      ]);
      setCalendarBookings(bookingsRes.data.calendar || {});
      setBlockedDates(blockedRes.data || []);
    } catch (error) {
    }
  };

  const toggleBlockDate = async (day) => {
    const dateStr = formatDateForAPI(day);
    const isBlocked = blockedDates.some(b => b.date === dateStr);
    
    try {
      if (isBlocked) {
        await axios.delete(`${API}/admin/blocked-dates/${dateStr}`, {
          headers: { Authorization: authHeader }
        });
        toast.success(`Fecha ${dateStr} desbloqueada`);
      } else {
        await axios.post(`${API}/admin/blocked-dates?date=${dateStr}&reason=Centro cerrado`, null, {
          headers: { Authorization: authHeader }
        });
        toast.success(`Fecha ${dateStr} bloqueada`);
      }
      fetchCalendarBookings(calendarDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado del día');
    }
  };

  const blockDateRange = async (e) => {
    e.preventDefault();
    if (!blockRange.start_date || !blockRange.end_date) {
      toast.error('Selecciona las fechas de inicio y fin');
      return;
    }
    if (blockingRange) return;
    setBlockingRange(true);
    try {
      const response = await axios.post(
        `${API}/admin/blocked-dates/range?start_date=${blockRange.start_date}&end_date=${blockRange.end_date}&reason=${encodeURIComponent(blockRange.reason)}`,
        null,
        { headers: { Authorization: authHeader }, timeout: 60000 }
      );
      toast.success(response.data.message);
      setIsBlockRangeOpen(false);
      setBlockRange({ start_date: '', end_date: '', reason: 'Centro cerrado' });
      fetchCalendarBookings(calendarDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al bloquear fechas');
    } finally {
      setBlockingRange(false);
    }
  };

  const unblockAllDates = async () => {
    if (!window.confirm('¿Desbloquear TODAS las fechas bloqueadas?')) return;
    try {
      const response = await axios.delete(`${API}/admin/blocked-dates`, {
        headers: { Authorization: authHeader }
      });
      toast.success(response.data.message);
      fetchCalendarBookings(calendarDate);
    } catch (error) {
      toast.error('Error al desbloquear fechas');
    }
  };

  const changeMonth = (delta) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCalendarDate(newDate);
  };

  const getCalendarDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
    
    const days = [];
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const formatDateForAPI = (day) => {
    const year = calendarDate.getFullYear();
    const month = String(calendarDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };

  const openManualBooking = (day) => {
    const dateStr = formatDateForAPI(day);
    setSelectedCalendarDate(dateStr);
    setManualBooking(prev => ({ ...prev, date: dateStr }));
    setIsManualBookingOpen(true);
  };

  const createManualBooking = async (e) => {
    e.preventDefault();
    if (!manualBooking.service_id || !manualBooking.date || !manualBooking.time || !manualBooking.customer_name || !manualBooking.customer_phone) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }
    const totalPeople = (manualBooking.num_adults || 0) + (manualBooking.num_children || 0);
    if (totalPeople <= 0) {
      toast.error('Indica al menos 1 adulto');
      return;
    }
    if (manualBooking.num_children > 0 && manualBooking.num_adults < 1) {
      toast.error('Los menores de 14 años deben ir acompañados por un adulto');
      return;
    }
    if (manualBooking.booking_type === 'adulto_bebe' && manualBooking.num_babies < 1) {
      toast.error('Indica al menos 1 bebé para el tipo "Adulto con bebé"');
      return;
    }
    try {
      const payload = { ...manualBooking, num_people: totalPeople };
      if (!payload.bono_code) delete payload.bono_code;
      // Normalise discount fields — empty strings should be omitted
      if (!payload.discount_promo_id) delete payload.discount_promo_id;
      if (!payload.discount_reason) delete payload.discount_reason;
      if (payload.manual_price === '' || payload.manual_price === undefined || payload.manual_price === null) {
        delete payload.manual_price;
      } else {
        payload.manual_price = parseFloat(payload.manual_price);
      }
      await axios.post(`${API}/admin/bookings/manual`, payload, {
        headers: { Authorization: authHeader }
      });
      toast.success(payload.bono_code ? `Reserva creada y 1 sesión descontada de ${payload.bono_code}` : 'Reserva manual creada');
      setIsManualBookingOpen(false);
      setManualBooking({
        service_id: '',
        date: '',
        time: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        num_adults: 1,
        num_children: 0,
        num_babies: 0,
        booking_type: 'solo_adultos',
        notes: '',
        bono_code: '',
        room: 'sala_grande',
        duration_minutes: 60,
        status: 'confirmed',
        discount_promo_id: '',
        manual_price: '',
        discount_reason: ''
      });
      setBonoSearchResults([]);
      setBonoSearchOpen(false);
      fetchCalendarBookings(calendarDate);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear reserva');
    }
  };

  // Search bonos for autocomplete (by code/name/email/phone)
  const searchBonos = async (query) => {
    if (!query || query.trim().length < 2) {
      setBonoSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/bonos/search?q=${encodeURIComponent(query.trim())}`, {
        headers: { Authorization: authHeader }
      });
      setBonoSearchResults(res.data);
    } catch {
      setBonoSearchResults([]);
    }
  };

  const selectBono = (bono) => {
    setManualBooking((p) => ({
      ...p,
      bono_code: bono.code,
      customer_name: p.customer_name || bono.customer_name,
      customer_phone: p.customer_phone || bono.customer_phone,
      customer_email: p.customer_email || (bono.customer_email || ''),
    }));
    setBonoSearchOpen(false);
    setBonoSearchResults([]);
    toast.success(`Bono ${bono.code} asociado`);
  };

  // Contact status functions
  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}/status?status=${status}`, {}, {
        headers: { Authorization: authHeader }
      });
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Customer functions
  const fetchCustomers = async () => {
    if (!authHeader) return;
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: authHeader }
      });
      setCustomers(response.data);
    } catch (error) {
    }
  };

  const fetchCustomerHistory = async (email) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${encodeURIComponent(email)}/history`, {
        headers: { Authorization: authHeader }
      });
      setCustomerHistory(response.data.bookings || []);
      setSelectedCustomer(email);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const updateCustomer = async (email, data) => {
    try {
      await axios.put(`${API}/admin/customers/${encodeURIComponent(email)}`, data, {
        headers: { Authorization: authHeader }
      });
      toast.success('Cliente actualizado');
      setIsCustomerDialogOpen(false);
      fetchCustomers();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar cliente');
    }
  };

  // Booking edit functions
  const openEditBooking = (booking) => {
    setEditingBooking({
      ...booking,
      newDate: booking.date,
      newTime: booking.time
    });
    setIsEditBookingOpen(true);
  };

  const openBookingDetails = (booking) => {
    setSelectedBookingDetail(booking);
    setIsBookingDetailOpen(true);
  };

  const saveBookingChanges = async () => {
    if (!editingBooking) return;
    try {
      await axios.put(`${API}/admin/bookings/${editingBooking.id}`, {
        date: editingBooking.newDate,
        time: editingBooking.newTime,
        customer_name: editingBooking.customer_name,
        customer_phone: editingBooking.customer_phone,
        notes: editingBooking.notes
      }, {
        headers: { Authorization: authHeader }
      });
      toast.success('Reserva actualizada');
      setIsEditBookingOpen(false);
      setEditingBooking(null);
      fetchData();
      fetchCalendarBookings(calendarDate);
    } catch (error) {
      toast.error('Error al actualizar reserva');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCalendarBookings(calendarDate);
      fetchCustomers();
    }
  }, [isAuthenticated, calendarDate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchRemindersStatus();
    }
  }, [isAuthenticated, userRole]);

  // Login screen
  if (!isAuthenticated) {
    return (
      <AdminLogin
        credentials={credentials}
        setCredentials={setCredentials}
        onSubmit={handleLogin}
      />
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-halo-cream" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-halo-charcoal text-halo-cream p-6 hidden lg:flex lg:flex-col">
        <div className="mb-8 shrink-0">
          <h1 className="font-heading text-2xl">HALO Admin</h1>
          <p className="text-halo-taupe text-sm mt-1">Panel de Control</p>
        </div>

        <nav className="space-y-2 flex-1 overflow-y-auto -mr-2 pr-2">
          {getAvailableTabs().map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-halo-brown text-halo-cream' 
                  : 'text-halo-taupe hover:bg-halo-cream/10 hover:text-halo-cream'
              }`}
              data-testid={`admin-tab-${item.id}`}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}

          <button
            onClick={handleLogout}
            className="w-full mt-4 flex items-center gap-3 px-4 py-3 rounded-lg text-halo-taupe hover:bg-red-500/20 hover:text-red-400 transition-colors border-t border-halo-taupe/20 pt-4"
            data-testid="admin-logout"
          >
            <LogOut size={20} />
            Cerrar Sesión
          </button>
        </nav>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden bg-halo-charcoal text-halo-cream p-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-xl">HALO Admin</h1>
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="bg-halo-brown text-halo-cream px-3 py-2 rounded-lg text-sm"
          >
            {getAvailableTabs().map(tab => (
              <option key={tab.id} value={tab.id}>{tab.label}</option>
            ))}
          </select>
        </div>
      </header>

      {/* Main content */}
      <main className="lg:ml-64 p-4 lg:p-8">
        {/* Tabs that render their own header + refresh button (we hide the page-level chrome) */}
        {(() => {
          const tabsWithOwnHeader = new Set([
            'bonos', 'customer-cards', 'invoices', 'walk-in', 'signatures',
            'internal-promos', 'help',
          ]);
          if (tabsWithOwnHeader.has(activeTab)) return null;
          return (
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl lg:text-3xl text-halo-charcoal capitalize">
                {(() => {
                  const labels = {
                    dashboard: 'Panel de Control',
                    calendar: 'Calendario',
                    content: 'Contenido',
                    services: 'Servicios',
                    bookings: 'Reservas',
                    customers: 'Clientes (legacy)',
                    promos: 'Códigos Promo',
                    contacts: 'Mensajes',
                  };
                  return labels[activeTab] || activeTab;
                })()}
              </h2>
              <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          );
        })()}

        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <DashboardTab
            stats={stats}
            bookings={bookings}
            userRole={userRole}
            remindersStatus={remindersStatus}
            sendingReminders={sendingReminders}
            onSendReminders={sendReminders}
          />
        )}

        {/* Bonos */}
        {activeTab === 'bonos' && (
          <BonosTab authHeader={authHeader} userRole={userRole} />
        )}

        {/* Customer Cards (Fase B) */}
        {activeTab === 'customer-cards' && (
          <CustomerCardsTab authHeader={authHeader} userRole={userRole} />
        )}

        {activeTab === 'invoices' && (
          <InvoicesTab authHeader={authHeader} />
        )}

        {activeTab === 'walk-in' && (
          <WalkInTab authHeader={authHeader} services={services} />
        )}

        {activeTab === 'signatures' && (
          <SignaturesTab authHeader={authHeader} />
        )}

        {activeTab === 'internal-promos' && (
          <InternalPromosTab authHeader={authHeader} userRole={userRole} />
        )}

        {activeTab === 'help' && <HelpTab />}

        {/* Calendar View */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            {/* Calendar Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => changeMonth(-1)}>
                  <ChevronLeft size={18} />
                </Button>
                <h3 className="font-heading text-xl text-halo-charcoal min-w-[200px] text-center">
                  {calendarDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </h3>
                <Button variant="outline" size="sm" onClick={() => changeMonth(1)}>
                  <ChevronRight size={18} />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {/* Room filter */}
                <div className="flex items-center gap-1 bg-halo-cream rounded-full p-0.5 mr-2" data-testid="room-filter">
                  {[
                    { id: 'all', label: 'Todas', icon: '📅' },
                    { id: 'sala_pequena', label: 'Sala 1', icon: '🏠' },
                    { id: 'sala_grande', label: 'Sala 2', icon: '🏛️' },
                  ].map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setRoomFilter(r.id)}
                      className={`px-3 py-1 rounded-full text-xs transition-colors ${
                        roomFilter === r.id
                          ? 'bg-halo-brown text-halo-cream'
                          : 'text-halo-charcoal hover:bg-halo-brown/10'
                      }`}
                      data-testid={`room-filter-${r.id}`}
                    >
                      {r.icon} {r.label}
                    </button>
                  ))}
                </div>
                <Button 
                  onClick={() => setCalendarDate(new Date())} 
                  variant="outline" 
                  size="sm"
                >
                  Hoy
                </Button>
                <Button
                  onClick={() => setIsBlockRangeOpen(true)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="block-range-btn"
                >
                  <Ban size={14} className="mr-1" />
                  Bloquear rango
                </Button>
                {blockedDates.length > 0 && (
                  <Button
                    onClick={unblockAllDates}
                    variant="outline"
                    size="sm"
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    data-testid="unblock-all-btn"
                  >
                    <Unlock size={14} className="mr-1" />
                    Desbloquear todo ({blockedDates.length})
                  </Button>
                )}
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="bg-halo-surface rounded-xl border border-halo-taupe/10 overflow-hidden">
              {/* Days header */}
              <div className="grid grid-cols-7 bg-halo-charcoal text-halo-cream">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
                  <div key={day} className="p-3 text-center text-sm font-medium">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7">
                {getCalendarDays().map((day, idx) => {
                  const dateStr = day ? formatDateForAPI(day) : null;
                  const dayBookings = dateStr ? filterByRoom(calendarBookings[dateStr] || []) : [];
                  const isToday = day && dateStr === new Date().toISOString().split('T')[0];
                  const isBlocked = dateStr && blockedDates.some(b => b.date === dateStr);
                  
                  return (
                    <div 
                      key={dateStr || `empty-${idx}`} 
                      className={`min-h-[120px] border-b border-r border-halo-taupe/10 p-2 ${
                        isBlocked ? 'bg-red-50' : day ? 'bg-white' : 'bg-gray-50'
                      } ${isToday ? 'ring-2 ring-halo-brown ring-inset' : ''}`}
                    >
                      {day && (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-medium ${isBlocked ? 'text-red-500' : isToday ? 'text-halo-brown' : 'text-halo-charcoal'}`}>
                              {day}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => toggleBlockDate(day)}
                                className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                                  isBlocked 
                                    ? 'bg-red-100 hover:bg-green-100 text-red-500 hover:text-green-600' 
                                    : 'bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500'
                                }`}
                                title={isBlocked ? 'Desbloquear día' : 'Bloquear día'}
                                data-testid={`toggle-block-${dateStr}`}
                              >
                                {isBlocked ? <Unlock size={10} /> : <Lock size={10} />}
                              </button>
                              {!isBlocked && (
                                <button
                                  onClick={() => openManualBooking(day)}
                                  className="w-5 h-5 rounded-full bg-halo-brown/10 hover:bg-halo-brown/20 flex items-center justify-center text-halo-brown"
                                  title="Añadir reserva"
                                >
                                  <Plus size={12} />
                                </button>
                              )}
                            </div>
                          </div>
                          {isBlocked ? (
                            <div className="flex items-center gap-1 text-xs text-red-500 mt-2 p-1.5 bg-red-100 rounded">
                              <Ban size={12} />
                              <span>Bloqueado</span>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {dayBookings.slice(0, 3).map((booking) => (
                                <div 
                                  key={booking.id}
                                  onClick={() => openBookingDetails(booking)}
                                  className={`text-xs p-1.5 rounded cursor-pointer transition-all hover:ring-2 hover:ring-halo-brown/50 ${
                                    booking.status === 'confirmed' 
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                      : booking.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                      : booking.status === 'completed'
                                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                  title={`Clic para ver detalles`}
                                >
                                  <span className="font-medium">{booking.time}</span> {booking.customer_name.split(' ')[0]}
                                  <span className="ml-1 text-[10px] opacity-70">{booking.room === 'sala_pequena' ? '🏠' : '🏛️'}</span>
                                </div>
                              ))}
                              {dayBookings.length > 3 && (
                                <button
                                  onClick={() => {
                                    setSelectedDayBookings(dayBookings);
                                    setSelectedDayDate(dateStr);
                                    setIsDayDetailOpen(true);
                                  }}
                                  className="text-xs text-halo-brown hover:underline"
                                >
                                  +{dayBookings.length - 3} más
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                <span className="text-halo-text">Confirmada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></div>
                <span className="text-halo-text">Pendiente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></div>
                <span className="text-halo-text">Completada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
                <span className="text-halo-text">Bloqueado</span>
              </div>
              <span className="text-halo-taupe">|</span>
              <span className="text-halo-text flex items-center gap-1">
                <Lock size={12} className="text-gray-400" /> = bloquear/desbloquear día
              </span>
              <span className="text-halo-taupe">|</span>
              <span className="text-halo-text flex items-center gap-1">
                <Plus size={12} className="text-halo-brown" /> = añadir reserva
              </span>
            </div>

            {/* Block Date Range Dialog */}
            <Dialog open={isBlockRangeOpen} onOpenChange={setIsBlockRangeOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Ban size={20} className="text-red-500" />
                    Bloquear rango de fechas
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={blockDateRange} className="space-y-4 mt-4">
                  <div>
                    <Label>Fecha inicio *</Label>
                    <Input 
                      type="date" 
                      value={blockRange.start_date}
                      onChange={(e) => setBlockRange(prev => ({ ...prev, start_date: e.target.value }))}
                      className="mt-2"
                      required
                      data-testid="block-range-start"
                    />
                  </div>
                  <div>
                    <Label>Fecha fin *</Label>
                    <Input 
                      type="date" 
                      value={blockRange.end_date}
                      onChange={(e) => setBlockRange(prev => ({ ...prev, end_date: e.target.value }))}
                      className="mt-2"
                      required
                      data-testid="block-range-end"
                    />
                  </div>
                  <div>
                    <Label>Motivo</Label>
                    <Input 
                      value={blockRange.reason}
                      onChange={(e) => setBlockRange(prev => ({ ...prev, reason: e.target.value }))}
                      className="mt-2"
                      placeholder="Centro cerrado"
                      data-testid="block-range-reason"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                    disabled={blockingRange}
                    data-testid="block-range-submit"
                  >
                    {blockingRange ? (
                      <>
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                        Bloqueando fechas...
                      </>
                    ) : (
                      <>
                        <Ban size={16} className="mr-2" />
                        Bloquear fechas
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Google Calendar Connection Panel */}
            <div className="bg-halo-surface rounded-xl border border-halo-taupe/10 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${calendarConnected ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Calendar size={20} className={calendarConnected ? 'text-green-600' : 'text-gray-400'} />
                  </div>
                  <div>
                    <h4 className="font-medium text-halo-charcoal">Google Calendar</h4>
                    {calendarConnected ? (
                      <p className="text-sm text-green-600">Conectado: {calendarEmail}</p>
                    ) : (
                      <p className="text-sm text-halo-taupe">No conectado</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {calendarConnected ? (
                    <>
                      <Button
                        onClick={syncCalendar}
                        variant="outline"
                        size="sm"
                        disabled={syncingCalendar}
                        data-testid="sync-calendar-btn"
                      >
                        <RefreshCw size={14} className={`mr-1 ${syncingCalendar ? 'animate-spin' : ''}`} />
                        {syncingCalendar ? 'Sincronizando...' : 'Sincronizar reservas'}
                      </Button>
                      <Button
                        onClick={disconnectGoogleCalendar}
                        variant="outline"
                        size="sm"
                        className="text-red-500 border-red-200 hover:bg-red-50"
                        data-testid="disconnect-calendar-btn"
                      >
                        Desconectar
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={connectGoogleCalendar}
                      size="sm"
                      className="bg-halo-brown hover:bg-halo-charcoal text-white"
                      data-testid="connect-calendar-btn"
                    >
                      Conectar Google Calendar
                    </Button>
                  )}
                </div>
              </div>
              {calendarConnected && (
                <p className="text-xs text-halo-taupe mt-3">Las nuevas reservas se añadirán automáticamente a Google Calendar. Usa "Sincronizar" para exportar reservas existentes.</p>
              )}
              
              {/* Manual code entry fallback */}
              {!calendarConnected && showManualCodeEntry && (
                <div className="mt-4 p-4 bg-halo-cream/50 rounded-lg border border-halo-taupe/10">
                  <p className="text-sm text-halo-charcoal mb-3 font-medium">Conexión manual:</p>
                  <p className="text-xs text-halo-text mb-3">
                    Si el botón de arriba no funciona, sigue estos pasos:
                    <br />1. Haz clic en "Abrir Google" abajo
                    <br />2. Autoriza el acceso con tu cuenta de Google
                    <br />3. Cuando Google te muestre "Esta app no está verificada", haz clic en <strong>"Avanzado"</strong> → <strong>"Ir a..."</strong>
                    <br />4. Copia el código de la URL (después de <code>code=</code>) y pégalo aquí
                  </p>
                  <div className="flex gap-2 mb-3">
                    <Button
                      onClick={async () => {
                        const response = await axios.get(`${API}/oauth/calendar/login`, {
                          headers: { Authorization: authHeader }
                        });
                        window.open(response.data.authorization_url, '_blank');
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Abrir Google
                    </Button>
                  </div>
                  <form onSubmit={connectWithManualCode} className="flex gap-2">
                    <Input
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      placeholder="Pega el código aquí..."
                      className="flex-1 text-sm"
                      data-testid="manual-code-input"
                    />
                    <Button 
                      type="submit" 
                      size="sm" 
                      className="bg-halo-brown hover:bg-halo-charcoal text-white"
                      disabled={connectingCalendar || !manualCode.trim()}
                      data-testid="manual-code-submit"
                    >
                      {connectingCalendar ? 'Conectando...' : 'Conectar'}
                    </Button>
                  </form>
                </div>
              )}
              
              {!calendarConnected && !showManualCodeEntry && (
                <button 
                  onClick={() => setShowManualCodeEntry(true)}
                  className="text-xs text-halo-taupe hover:text-halo-brown mt-3 underline"
                >
                  ¿Problemas para conectar? Prueba el método manual
                </button>
              )}
            </div>

            {/* Manual Booking Dialog */}
            <Dialog open={isManualBookingOpen} onOpenChange={setIsManualBookingOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Nueva Reserva Manual</DialogTitle>
                </DialogHeader>
                <form onSubmit={createManualBooking} className="space-y-4 mt-4">
                  <div className="bg-halo-brown/10 p-3 rounded-lg text-sm text-halo-charcoal">
                    📅 Fecha: <strong>{selectedCalendarDate}</strong> · Pago al llegar
                  </div>
                  
                  <div>
                    <Label>Servicio *</Label>
                    <Select 
                      value={manualBooking.service_id}
                      onValueChange={(val) => setManualBooking(prev => ({ ...prev, service_id: val }))}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Selecciona servicio" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} - {s.price}€</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Hora *</Label>
                      <Select 
                        value={manualBooking.time}
                        onValueChange={(val) => setManualBooking(prev => ({ ...prev, time: val }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Selecciona hora" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {(() => {
                            const slots = [];
                            for (let h = 9; h <= 21; h++) {
                              for (const m of [0, 15, 30, 45]) {
                                if (h === 21 && m > 0) continue;
                                slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                              }
                            }
                            return slots.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Duración</Label>
                      <Select
                        value={String(manualBooking.duration_minutes)}
                        onValueChange={(val) => setManualBooking(prev => ({ ...prev, duration_minutes: parseInt(val) }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[15, 30, 45, 60, 75, 90, 120].map(m => (
                            <SelectItem key={m} value={String(m)}>
                              {m < 60 ? `${m} min` : m === 60 ? '1 hora' : m === 90 ? '1h 30min' : m === 120 ? '2 horas' : `${m} min`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sala</Label>
                      <Select
                        value={manualBooking.room}
                        onValueChange={(val) => setManualBooking(prev => ({ ...prev, room: val }))}
                        disabled={manualBooking.num_children > 0}
                      >
                        <SelectTrigger className="mt-2" data-testid="manual-booking-room">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sala_grande">🏛️ Sala 2 (grande) - máx 9 adultos</SelectItem>
                          <SelectItem value="sala_pequena">🏠 Sala 1 (pequeña) - máx 2 ad + 3 niños</SelectItem>
                        </SelectContent>
                      </Select>
                      {manualBooking.num_children > 0 && (
                        <p className="text-xs text-halo-text mt-1">Asignada automáticamente al haber niños</p>
                      )}
                    </div>
                    <div>
                      <Label>Estado</Label>
                      <Select
                        value={manualBooking.status}
                        onValueChange={(val) => setManualBooking(prev => ({ ...prev, status: val }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">✓ Confirmada</SelectItem>
                          <SelectItem value="pending">⏳ Pendiente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre cliente *</Label>
                      <Input 
                        value={manualBooking.customer_name}
                        onChange={(e) => setManualBooking(prev => ({ ...prev, customer_name: e.target.value }))}
                        className="mt-2"
                        placeholder="Nombre completo"
                      />
                    </div>
                    <div>
                      <Label>Teléfono *</Label>
                      <Input 
                        value={manualBooking.customer_phone}
                        onChange={(e) => setManualBooking(prev => ({ ...prev, customer_phone: e.target.value }))}
                        className="mt-2"
                        placeholder="612 345 678"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Email (opcional)</Label>
                    <Input 
                      type="email"
                      value={manualBooking.customer_email}
                      onChange={(e) => setManualBooking(prev => ({ ...prev, customer_email: e.target.value }))}
                      className="mt-2"
                      placeholder="cliente@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label>Tipo de reserva *</Label>
                    <Select
                      value={manualBooking.booking_type}
                      onValueChange={(val) => setManualBooking(prev => ({
                        ...prev,
                        booking_type: val,
                        // Reset counts when changing type
                        num_children: val === 'solo_adultos' ? 0 : prev.num_children,
                        num_babies: val === 'adulto_bebe' ? Math.max(prev.num_babies, 1) : 0,
                        // Auto-room: anything with kids/baby → small room
                        room: val === 'solo_adultos' ? prev.room : 'sala_pequena',
                      }))}
                    >
                      <SelectTrigger className="mt-2" data-testid="manual-booking-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solo_adultos">Solo adultos</SelectItem>
                        <SelectItem value="con_ninos">Con niños (hasta 14 años)</SelectItem>
                        <SelectItem value="adulto_bebe">Adulto con bebé (menor de 12 meses)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Adultos *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={manualBooking.num_adults}
                        onChange={(e) => setManualBooking(prev => ({ ...prev, num_adults: parseInt(e.target.value) || 1 }))}
                        className="mt-2"
                        data-testid="manual-booking-adults"
                      />
                    </div>
                    <div>
                      <Label>Niños <span className="text-xs text-halo-text">(hasta 14)</span></Label>
                      <Input
                        type="number"
                        min="0"
                        disabled={manualBooking.booking_type !== 'con_ninos'}
                        value={manualBooking.num_children}
                        onChange={(e) => {
                          const num_children = parseInt(e.target.value) || 0;
                          setManualBooking(prev => ({
                            ...prev,
                            num_children,
                            room: num_children > 0 ? 'sala_pequena' : prev.room,
                          }));
                        }}
                        className="mt-2"
                        data-testid="manual-booking-children"
                      />
                    </div>
                    <div>
                      <Label>Bebés <span className="text-xs text-halo-text">(&lt;12m)</span></Label>
                      <Input
                        type="number"
                        min="0"
                        disabled={manualBooking.booking_type !== 'adulto_bebe'}
                        value={manualBooking.num_babies}
                        onChange={(e) => setManualBooking(prev => ({ ...prev, num_babies: parseInt(e.target.value) || 0 }))}
                        className="mt-2"
                        data-testid="manual-booking-babies"
                      />
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
                    ⚠️ Los menores de 14 años deben ir acompañados <strong>siempre</strong> por un adulto. Tanto adultos como niños deben adquirir su bono.
                  </div>

                  {/* Bono linkage */}
                  <div className="bg-halo-cream/40 rounded-lg p-4 space-y-2 relative">
                    <Label className="flex items-center gap-2">
                      <Ticket size={14} className="text-halo-brown" />
                      Asociar a un bono <span className="text-halo-text font-normal text-xs">(opcional)</span>
                    </Label>
                    <Input
                      placeholder="Buscar por código, nombre, email o teléfono..."
                      value={manualBooking.bono_code}
                      onChange={(e) => {
                        const v = e.target.value;
                        setManualBooking((p) => ({ ...p, bono_code: v }));
                        setBonoSearchOpen(true);
                        searchBonos(v);
                      }}
                      onFocus={() => setBonoSearchOpen(true)}
                      onBlur={() => setTimeout(() => setBonoSearchOpen(false), 200)}
                      data-testid="manual-booking-bono"
                    />
                    {bonoSearchOpen && bonoSearchResults.length > 0 && (
                      <div className="absolute left-4 right-4 top-full mt-1 bg-white border border-halo-taupe/20 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                        {bonoSearchResults.map((b) => (
                          <button
                            key={b.id}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectBono(b);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-halo-cream/50 border-b border-halo-taupe/10 last:border-b-0"
                            data-testid={`bono-option-${b.code}`}
                          >
                            <div className="flex justify-between items-center text-sm">
                              <span className="font-mono font-bold text-halo-brown">{b.code}</span>
                              <span className="text-xs text-halo-text">{b.sessions_remaining}/{b.sessions_total} ses</span>
                            </div>
                            <div className="text-xs text-halo-text">
                              {b.customer_name} · {b.customer_phone} · {b.bono_name}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {manualBooking.bono_code && (
                      <p className="text-xs text-halo-text">
                        Al crear la reserva, se descontará 1 sesión del bono <strong className="text-halo-brown">{manualBooking.bono_code}</strong>.
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>Notas (opcional)</Label>
                    <Textarea 
                      value={manualBooking.notes}
                      onChange={(e) => setManualBooking(prev => ({ ...prev, notes: e.target.value }))}
                      className="mt-2"
                      placeholder="Notas adicionales..."
                      rows={2}
                    />
                  </div>

                  {/* Internal-only discount — not shown on public web */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3" data-testid="manual-discount-block">
                    <Label className="flex items-center gap-2 text-amber-900 font-medium">
                      <Tag size={14} /> Descuento interno
                      <span className="text-xs font-normal text-amber-800">(no aparece en la web)</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Promo interna</Label>
                        <select
                          value={manualBooking.discount_promo_id || ''}
                          onChange={(e) => {
                            const id = e.target.value;
                            const p = internalPromos.find((x) => x.id === id);
                            setManualBooking((prev) => ({
                              ...prev,
                              discount_promo_id: id,
                              discount_reason: p ? p.label : prev.discount_reason,
                            }));
                          }}
                          className="w-full mt-1 rounded-md border border-amber-300 px-2 py-2 bg-white text-sm"
                          data-testid="manual-promo-select"
                        >
                          <option value="">— Sin promo / personalizado —</option>
                          {internalPromos.filter((p) => p.active && !p.expired).map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code ? `[${p.code}] ` : ''}
                              {p.label} ({p.discount_type === 'percent'
                                ? `-${p.discount_value}%`
                                : p.discount_type === 'final_price'
                                ? `→ ${p.discount_value}€`
                                : `-${p.discount_value}€`})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Precio final manual (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="opcional"
                          value={manualBooking.manual_price}
                          onChange={(e) => setManualBooking((prev) => ({ ...prev, manual_price: e.target.value }))}
                          data-testid="manual-price-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Motivo / etiqueta del descuento</Label>
                      <Input
                        placeholder="Ej: Flyer marzo, cortesía..."
                        value={manualBooking.discount_reason}
                        onChange={(e) => setManualBooking((prev) => ({ ...prev, discount_reason: e.target.value }))}
                        data-testid="manual-discount-reason"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1 bg-halo-brown hover:bg-halo-charcoal">
                      Crear Reserva
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsManualBookingOpen(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Booking Detail Dialog */}
            <Dialog open={isBookingDetailOpen} onOpenChange={setIsBookingDetailOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Detalle de Reserva</DialogTitle>
                </DialogHeader>
                {selectedBookingDetail && (
                  <div className="space-y-4 mt-4">
                    <div className={`p-3 rounded-lg text-center ${
                      selectedBookingDetail.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      selectedBookingDetail.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      selectedBookingDetail.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      <span className="font-medium capitalize">{selectedBookingDetail.status}</span>
                    </div>
                    
                    <div className="bg-halo-cream rounded-lg p-4 space-y-3">
                      <div>
                        <p className="text-xs text-halo-taupe uppercase tracking-wider">Servicio</p>
                        <p className="font-medium text-halo-charcoal">{selectedBookingDetail.service_name}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-halo-taupe uppercase tracking-wider">Fecha</p>
                          <p className="font-medium text-halo-charcoal">{selectedBookingDetail.date}</p>
                        </div>
                        <div>
                          <p className="text-xs text-halo-taupe uppercase tracking-wider">Hora</p>
                          <p className="font-medium text-halo-charcoal">{selectedBookingDetail.time}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-halo-taupe uppercase tracking-wider">Cliente</p>
                        <p className="font-medium text-halo-charcoal">{selectedBookingDetail.customer_name}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-halo-taupe uppercase tracking-wider">Personas</p>
                          <p className="font-medium text-halo-charcoal">{selectedBookingDetail.num_people}</p>
                        </div>
                        <div>
                          <p className="text-xs text-halo-taupe uppercase tracking-wider">Total</p>
                          <p className="font-medium text-halo-brown">{selectedBookingDetail.total_price}€</p>
                        </div>
                      </div>
                      {selectedBookingDetail.payment_method === 'local' && (
                        <div className="text-xs bg-yellow-50 text-yellow-700 p-2 rounded">
                          💰 Pago al llegar
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          // Find full booking from bookings list
                          const fullBooking = bookings.find(b => b.id === selectedBookingDetail.id);
                          if (fullBooking) {
                            openEditBooking(fullBooking);
                          }
                          setIsBookingDetailOpen(false);
                        }}
                      >
                        <Edit2 size={14} className="mr-1" /> Editar
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => setIsBookingDetailOpen(false)}
                      >
                        Cerrar
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Day Detail Dialog (for +X más) */}
            <Dialog open={isDayDetailOpen} onOpenChange={setIsDayDetailOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Reservas del {selectedDayDate}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 mt-4 max-h-[60vh] overflow-y-auto">
                  {selectedDayBookings.map((booking) => (
                    <div 
                      key={booking.id}
                      onClick={() => {
                        openBookingDetails(booking);
                        setIsDayDetailOpen(false);
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-all hover:ring-2 hover:ring-halo-brown/50 ${
                        booking.status === 'confirmed' ? 'bg-green-50' :
                        booking.status === 'pending' ? 'bg-yellow-50' :
                        'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-halo-charcoal">{booking.time}</span>
                          <span className="text-halo-text ml-2">{booking.customer_name}</span>
                        </div>
                        <span className="text-sm text-halo-brown">{booking.total_price}€</span>
                      </div>
                      <p className="text-xs text-halo-taupe mt-1">{booking.service_name}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={() => {
                      openManualBooking(parseInt(selectedDayDate?.split('-')[2]));
                      setIsDayDetailOpen(false);
                    }}
                    className="flex-1 bg-halo-brown hover:bg-halo-charcoal"
                  >
                    <Plus size={14} className="mr-1" /> Añadir Reserva
                  </Button>
                  <Button variant="outline" onClick={() => setIsDayDetailOpen(false)}>
                    Cerrar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Content Editor */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="bg-halo-brown/10 rounded-xl p-4 mb-6">
              <p className="text-halo-charcoal text-sm">
                <strong>💡 Tip:</strong> Aquí puedes editar los textos e imágenes de las páginas principales de tu web.
              </p>
            </div>
            
            {content.length === 0 ? (
              <p className="text-center text-halo-text py-8">Cargando contenido...</p>
            ) : (
              Object.entries(
                content.reduce((acc, item) => {
                  if (!acc[item.page]) acc[item.page] = [];
                  acc[item.page].push(item);
                  return acc;
                }, {})
              ).map(([page, items]) => (
                <div key={page} className="bg-halo-surface rounded-xl p-6 border border-halo-taupe/10">
                  <h3 className="font-heading text-xl text-halo-charcoal mb-4 capitalize border-b border-halo-taupe/20 pb-2">
                    Página: {page === 'home' ? 'Inicio' : page}
                  </h3>
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.id} className="bg-halo-cream rounded-lg p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <span className="text-xs font-medium text-halo-brown uppercase tracking-wider">
                              {item.section}
                            </span>
                            {item.title && (
                              <h4 className="font-heading text-lg text-halo-charcoal mt-1">{item.title}</h4>
                            )}
                            {item.text && (
                              <p className="text-halo-text text-sm mt-2 line-clamp-2">{item.text}</p>
                            )}
                            {item.image_url && (
                              <div className="mt-2 flex items-center gap-2">
                                <Image size={14} className="text-halo-taupe" />
                                <span className="text-xs text-halo-taupe truncate max-w-xs">{item.image_url}</span>
                              </div>
                            )}
                          </div>
                          <Dialog open={isContentDialogOpen && editingContent?.id === item.id} onOpenChange={(open) => {
                            setIsContentDialogOpen(open);
                            if (!open) setEditingContent(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setEditingContent(item)}
                              >
                                <Edit2 size={16} />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Editar Contenido - {item.section}</DialogTitle>
                              </DialogHeader>
                              {editingContent && (
                                <ContentEditForm 
                                  content={editingContent} 
                                  onSave={(data) => updateContent(editingContent.id, data)}
                                  onCancel={() => {
                                    setIsContentDialogOpen(false);
                                    setEditingContent(null);
                                  }}
                                />
                              )}
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Services */}
        {activeTab === 'services' && (
          <div className="space-y-4">
            {/* Create Service Button */}
            <div className="flex justify-end">
              <Button
                onClick={() => setIsCreateServiceOpen(true)}
                className="bg-halo-brown hover:bg-halo-charcoal text-white"
                data-testid="create-service-btn"
              >
                <Plus size={16} className="mr-2" />
                Nuevo servicio
              </Button>
            </div>

            {services.map((service) => (
              <div key={service.id} className="bg-halo-surface rounded-xl p-4 lg:p-6 border border-halo-taupe/10">
                <div className="flex flex-col lg:flex-row gap-4">
                  <img 
                    src={service.image_url} 
                    alt={service.name}
                    className="w-full lg:w-32 h-32 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading text-lg text-halo-charcoal">{service.name}</h3>
                        <p className="text-halo-text text-sm mt-1 line-clamp-2">{service.description}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Dialog open={isDialogOpen && editingService?.id === service.id} onOpenChange={(open) => {
                          setIsDialogOpen(open);
                          if (!open) setEditingService(null);
                        }}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setEditingService(service)}
                              data-testid={`edit-service-${service.id}`}
                            >
                              <Edit2 size={16} />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Editar Servicio</DialogTitle>
                            </DialogHeader>
                            {editingService && (
                              <ServiceEditForm 
                                service={editingService} 
                                onSave={(data) => updateService(editingService.id, data)}
                                onCancel={() => {
                                  setIsDialogOpen(false);
                                  setEditingService(null);
                                }}
                              />
                            )}
                          </DialogContent>
                        </Dialog>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteService(service.id, service.name)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-service-${service.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <span className="flex items-center gap-1 text-sm text-halo-text">
                        <Clock size={14} /> {service.duration} min
                      </span>
                      <span className="flex items-center gap-1 text-sm font-medium text-halo-brown">
                        <Euro size={14} /> {service.price}
                      </span>
                      <span className="text-xs px-2 py-1 bg-halo-taupe/20 rounded-full">
                        {service.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Create Service Dialog */}
            <Dialog open={isCreateServiceOpen} onOpenChange={setIsCreateServiceOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nuevo Servicio</DialogTitle>
                </DialogHeader>
                <ServiceCreateForm 
                  onSave={createService}
                  onCancel={() => setIsCreateServiceOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Bookings */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <p className="text-center text-halo-text py-8">No hay reservas</p>
            ) : (
              bookings.map((booking) => (
                <div key={booking.id} className="bg-halo-surface rounded-xl p-4 lg:p-6 border border-halo-taupe/10">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-lg text-halo-charcoal">{booking.customer_name}</h3>
                      <p className="text-halo-text text-sm">{booking.customer_email} · {booking.customer_phone}</p>
                      <p className="text-halo-brown text-sm mt-1">{booking.service_name}</p>
                      <p className="text-halo-text text-sm">{booking.date} a las {booking.time} · {booking.num_people} pers.</p>
                      {booking.promo_code && (
                        <p className="text-green-600 text-xs mt-1">
                          Código aplicado: {booking.promo_code} (-{booking.discount_percent}%)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-heading text-lg text-halo-charcoal">{booking.total_price}€</p>
                        <p className="text-xs text-halo-text">Depósito: {booking.deposit_amount}€</p>
                      </div>
                      <Select
                        value={booking.status}
                        onValueChange={(value) => updateBookingStatus(booking.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendiente</SelectItem>
                          <SelectItem value="confirmed">Confirmada</SelectItem>
                          <SelectItem value="completed">Completada</SelectItem>
                          <SelectItem value="cancelled">Cancelada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {booking.notes && (
                    <p className="mt-3 text-sm text-halo-text bg-halo-cream p-3 rounded-lg">
                      <strong>Notas:</strong> {booking.notes}
                    </p>
                  )}
                  {/* Action buttons */}
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-halo-taupe/10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditBooking(booking)}
                      className="text-xs"
                      data-testid={`edit-booking-${booking.id}`}
                    >
                      <Edit2 size={14} className="mr-1" /> Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resendConfirmationEmail(booking.id)}
                      className="text-xs"
                      data-testid={`resend-email-${booking.id}`}
                    >
                      <RefreshCw size={14} className="mr-1" /> Reenviar Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadInvoice(booking.id)}
                      className="text-xs"
                      data-testid={`download-invoice-${booking.id}`}
                    >
                      <FileText size={14} className="mr-1" /> Descargar Factura
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteBooking(booking.id)}
                        className="text-xs text-red-500 hover:bg-red-50 hover:text-red-600"
                        data-testid={`delete-booking-${booking.id}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Eliminar
                      </Button>
                  </div>
                </div>
              ))
            )}
            
            {/* Edit Booking Dialog */}
            <Dialog open={isEditBookingOpen} onOpenChange={setIsEditBookingOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Editar Reserva</DialogTitle>
                </DialogHeader>
                {editingBooking && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-halo-cream rounded-lg p-3 text-sm">
                      <p className="font-medium text-halo-charcoal">{editingBooking.service_name}</p>
                      <p className="text-halo-text">{editingBooking.customer_name}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Fecha</Label>
                        <Input 
                          type="date"
                          value={editingBooking.newDate}
                          onChange={(e) => setEditingBooking(prev => ({ ...prev, newDate: e.target.value }))}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Hora</Label>
                        <Select 
                          value={editingBooking.newTime}
                          onValueChange={(val) => setEditingBooking(prev => ({ ...prev, newTime: val }))}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['10:00', '11:00', '12:00', '13:00', '16:00', '17:00', '18:00', '19:00'].map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Nombre cliente</Label>
                      <Input 
                        value={editingBooking.customer_name}
                        onChange={(e) => setEditingBooking(prev => ({ ...prev, customer_name: e.target.value }))}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label>Teléfono</Label>
                      <Input 
                        value={editingBooking.customer_phone || ''}
                        onChange={(e) => setEditingBooking(prev => ({ ...prev, customer_phone: e.target.value }))}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label>Notas</Label>
                      <Textarea 
                        value={editingBooking.notes || ''}
                        onChange={(e) => setEditingBooking(prev => ({ ...prev, notes: e.target.value }))}
                        className="mt-2"
                        rows={2}
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button onClick={saveBookingChanges} className="flex-1 bg-halo-brown hover:bg-halo-charcoal">
                        Guardar Cambios
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditBookingOpen(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Customers */}
        {activeTab === 'customers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading text-halo-charcoal">
                Clientes ({customers.length})
              </h2>
            </div>
            
            {customers.length === 0 ? (
              <p className="text-center text-halo-text py-8">No hay clientes registrados</p>
            ) : (
              <div className="space-y-4">
                {customers.map((customer) => (
                  <div 
                    key={customer.email} 
                    className="bg-halo-surface rounded-xl p-4 lg:p-6 border border-halo-taupe/10"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-halo-brown/10 flex items-center justify-center">
                          <User size={24} className="text-halo-brown" />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg text-halo-charcoal">{customer.name || 'Sin nombre'}</h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-halo-text mt-1">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {customer.email}
                            </span>
                            {customer.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={14} /> {customer.phone}
                              </span>
                            )}
                          </div>
                          {customer.notes && (
                            <p className="text-xs text-halo-taupe mt-1 italic">{customer.notes}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-center px-4 border-r border-halo-taupe/20">
                          <p className="font-heading text-2xl text-halo-charcoal">{customer.total_bookings}</p>
                          <p className="text-xs text-halo-text">Reservas</p>
                        </div>
                        <div className="text-center px-4">
                          <p className="font-heading text-2xl text-halo-brown">{customer.total_spent}€</p>
                          <p className="text-xs text-halo-text">Gastado</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingCustomer(customer);
                              setIsCustomerDialogOpen(true);
                            }}
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchCustomerHistory(customer.email)}
                          >
                            <History size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Show history if selected */}
                    {selectedCustomer === customer.email && customerHistory.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-halo-taupe/10">
                        <h4 className="text-sm font-medium text-halo-charcoal mb-3">Historial de reservas:</h4>
                        <div className="space-y-2">
                          {customerHistory.map((booking) => (
                            <div key={booking.id} className="flex items-center justify-between bg-halo-cream rounded-lg p-3 text-sm">
                              <div>
                                <span className="font-medium text-halo-charcoal">{booking.service_name}</span>
                                <span className="text-halo-text ml-2">{booking.date} - {booking.time}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-halo-brown font-medium">{booking.total_price}€</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                  booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                  booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {booking.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-xs"
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerHistory([]);
                          }}
                        >
                          Cerrar historial
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Edit Customer Dialog */}
            <Dialog open={isCustomerDialogOpen} onOpenChange={setIsCustomerDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Editar Cliente</DialogTitle>
                </DialogHeader>
                {editingCustomer && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-halo-cream rounded-lg p-3 text-sm text-halo-text">
                      Email: {editingCustomer.email}
                    </div>
                    
                    <div>
                      <Label>Nombre</Label>
                      <Input 
                        value={editingCustomer.name || ''}
                        onChange={(e) => setEditingCustomer(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label>Teléfono</Label>
                      <Input 
                        value={editingCustomer.phone || ''}
                        onChange={(e) => setEditingCustomer(prev => ({ ...prev, phone: e.target.value }))}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label>Notas internas</Label>
                      <Textarea 
                        value={editingCustomer.notes || ''}
                        onChange={(e) => setEditingCustomer(prev => ({ ...prev, notes: e.target.value }))}
                        className="mt-2"
                        placeholder="Notas sobre el cliente..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button 
                        onClick={() => updateCustomer(editingCustomer.email, {
                          name: editingCustomer.name,
                          phone: editingCustomer.phone,
                          notes: editingCustomer.notes
                        })} 
                        className="flex-1 bg-halo-brown hover:bg-halo-charcoal"
                      >
                        Guardar
                      </Button>
                      <Button variant="outline" onClick={() => setIsCustomerDialogOpen(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Promo Codes */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading text-halo-charcoal">Códigos Promocionales</h2>
              <Dialog open={isPromoDialogOpen} onOpenChange={setIsPromoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-halo-brown hover:bg-halo-charcoal">
                    <Plus size={18} className="mr-2" /> Crear Código
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Crear Código Promocional</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createPromoCode} className="space-y-4 mt-4">
                    <div>
                      <Label>Código</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newPromoCode.code}
                          onChange={(e) => setNewPromoCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                          placeholder="INVITACION2345"
                          className="font-mono"
                        />
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setNewPromoCode(prev => ({ ...prev, code: generateRandomCode('INV') }))}
                        >
                          Generar
                        </Button>
                      </div>
                      <p className="text-xs text-halo-text mt-1">Usa el botón para generar un código aleatorio</p>
                    </div>
                    <div>
                      <Label>Tipo de descuento</Label>
                      <Select
                        value={newPromoCode.discount_type}
                        onValueChange={(val) => setNewPromoCode(prev => ({ ...prev, discount_type: val }))}
                      >
                        <SelectTrigger className="mt-2" data-testid="promo-discount-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Porcentaje (%)</SelectItem>
                          <SelectItem value="fixed">Importe fijo (€)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newPromoCode.discount_type === 'fixed' ? (
                      <div>
                        <Label>Importe a descontar (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={newPromoCode.discount_amount}
                          onChange={(e) => setNewPromoCode(prev => ({ ...prev, discount_amount: e.target.value }))}
                          placeholder="Ej: 5"
                          className="mt-2"
                          data-testid="promo-discount-amount"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label>Descuento (%)</Label>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          value={newPromoCode.discount_percent}
                          onChange={(e) => setNewPromoCode(prev => ({ ...prev, discount_percent: e.target.value }))}
                          placeholder="10"
                          className="mt-2"
                          data-testid="promo-discount-percent"
                        />
                      </div>
                    )}
                    <div>
                      <Label>
                        Válido hasta{' '}
                        <span className="text-xs font-normal text-halo-text">
                          (opcional)
                        </span>
                      </Label>
                      <Input
                        type="date"
                        value={newPromoCode.valid_until}
                        onChange={(e) => setNewPromoCode(prev => ({ ...prev, valid_until: e.target.value }))}
                        className="mt-2"
                        data-testid="promo-valid-until"
                      />
                    </div>
                    <div>
                      <Label>Descripción (opcional)</Label>
                      <Input
                        value={newPromoCode.description}
                        onChange={(e) => setNewPromoCode(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Invitación especial para..."
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Límite de usos (vacío = ilimitado)</Label>
                      <Input
                        type="number"
                        value={newPromoCode.max_uses}
                        onChange={(e) => setNewPromoCode(prev => ({ ...prev, max_uses: e.target.value }))}
                        placeholder="Ej: 1 para invitación única"
                        className="mt-2"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1 bg-halo-brown hover:bg-halo-charcoal">
                        Crear Código
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setIsPromoDialogOpen(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Quick generate buttons */}
            <div className="bg-halo-surface rounded-xl p-4 border border-halo-taupe/10">
              <p className="text-sm text-halo-text mb-3">Generar código rápido:</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewPromoCode({
                      code: generateRandomCode('INV'),
                      discount_type: 'percent',
                      discount_percent: 100,
                      discount_amount: 0,
                      description: 'Invitación especial - Sesión gratuita',
                      max_uses: '1',
                      valid_until: '',
                    });
                    setIsPromoDialogOpen(true);
                  }}
                >
                  <Gift size={14} className="mr-1" /> Invitación 100% gratis
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewPromoCode({
                      code: generateRandomCode('PROMO'),
                      discount_type: 'percent',
                      discount_percent: 10,
                      discount_amount: 0,
                      description: 'Código promocional',
                      max_uses: '',
                      valid_until: '',
                    });
                    setIsPromoDialogOpen(true);
                  }}
                >
                  <Tag size={14} className="mr-1" /> Descuento 10%
                </Button>
              </div>
            </div>

            {/* Codes list */}
            {promoCodes.length === 0 ? (
              <p className="text-center text-halo-text py-8">No hay códigos promocionales</p>
            ) : (
              <div className="grid gap-4">
                {promoCodes.map((code) => {
                  const isFixed = (code.discount_type || 'percent') === 'fixed';
                  const isFree = !isFixed && code.discount_percent === 100;
                  const label = isFixed
                    ? `-${(code.discount_amount || 0)}€`
                    : isFree
                      ? 'GRATIS'
                      : `${code.discount_percent}% descuento`;
                  return (
                  <div 
                    key={code.id} 
                    className={`bg-halo-surface rounded-xl p-4 border ${code.is_active ? 'border-halo-taupe/10' : 'border-red-200 bg-red-50/30'} ${code.expired ? 'opacity-70' : ''}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isFree
                            ? 'bg-halo-sage/20 text-halo-sage' 
                            : 'bg-halo-brown/10 text-halo-brown'
                        }`}>
                          {isFree ? <Gift size={20} /> : <Tag size={20} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-halo-charcoal">{code.code}</span>
                            <button 
                              onClick={() => copyToClipboard(code.code)}
                              className="text-halo-taupe hover:text-halo-brown"
                            >
                              <Copy size={14} />
                            </button>
                            {code.expired && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                                Caducado
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-halo-text">
                            {label}
                            {code.valid_until ? ` · hasta ${String(code.valid_until).slice(0, 10)}` : ''}
                            {code.description && ` · ${code.description}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-halo-charcoal">
                            {code.current_uses || 0} {code.max_uses ? `/ ${code.max_uses}` : ''} usos
                          </p>
                          {!code.is_active && <span className="text-red-500 text-xs">Desactivado</span>}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => togglePromoCode(code.id, code.is_active)}
                          >
                            {code.is_active ? <X size={14} /> : <Check size={14} />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deletePromoCode(code.id)}
                            className="text-red-500 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Contacts */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {/* Filter tabs */}
            <div className="flex gap-2 mb-4">
              {[
                { id: 'all', label: 'Todos' },
                { id: 'nuevo', label: '🔵 Nuevos' },
                { id: 'en_espera', label: '🟡 En espera' },
                { id: 'contestado', label: '✅ Contestados' }
              ].map(filter => (
                <Button
                  key={filter.id}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => {/* Could add filter state */}}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
            
            {contacts.length === 0 ? (
              <p className="text-center text-halo-text py-8">No hay mensajes</p>
            ) : (
              contacts.map((contact) => (
                <div 
                  key={contact.id} 
                  className={`bg-halo-surface rounded-xl p-4 lg:p-6 border ${
                    contact.status === 'contestado' ? 'border-green-200 bg-green-50/30' :
                    contact.status === 'en_espera' ? 'border-yellow-200 bg-yellow-50/30' :
                    'border-halo-taupe/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading text-lg text-halo-charcoal">{contact.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          contact.status === 'contestado' ? 'bg-green-100 text-green-700' :
                          contact.status === 'en_espera' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {contact.status === 'contestado' ? 'Contestado' : 
                           contact.status === 'en_espera' ? 'En espera' : 'Nuevo'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-halo-text">
                        <span className="flex items-center gap-1">
                          <Mail size={14} /> {contact.email}
                        </span>
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} /> {contact.phone}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-halo-taupe mt-1">
                        {new Date(contact.created_at).toLocaleDateString('es-ES', { 
                          day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={contact.status || 'nuevo'}
                        onValueChange={(value) => updateContactStatus(contact.id, value)}
                      >
                        <SelectTrigger className="w-36 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nuevo">🔵 Nuevo</SelectItem>
                          <SelectItem value="en_espera">🟡 En espera</SelectItem>
                          <SelectItem value="contestado">✅ Contestado</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => deleteContact(contact.id)}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <p className="mt-4 text-halo-charcoal bg-halo-cream p-4 rounded-lg whitespace-pre-wrap">
                    {contact.message}
                  </p>
                  {/* Quick actions */}
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => window.open(`mailto:${contact.email}?subject=Re: Consulta HALO Cuevas de Sal`, '_blank')}
                    >
                      <Mail size={14} className="mr-1" /> Responder por email
                    </Button>
                    {contact.phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => window.open(`tel:${contact.phone}`, '_blank')}
                      >
                        <Phone size={14} className="mr-1" /> Llamar
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Service edit form component
const ServiceEditForm = ({ service, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: service.name,
    description: service.description,
    duration: service.duration,
    price: service.price,
    price_premium: service.price_premium || '',
    price_note: service.price_note || '',
    category: service.category,
    image_url: service.image_url || '',
    benefits: service.benefits?.join(', ') || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      name: form.name,
      description: form.description,
      duration: parseInt(form.duration),
      price: parseFloat(form.price),
      category: form.category,
      image_url: form.image_url,
      benefits: form.benefits.split(',').map(b => b.trim()).filter(b => b)
    };
    if (form.price_premium) data.price_premium = parseFloat(form.price_premium);
    if (form.price_note) data.price_note = form.price_note;
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Nombre</Label>
        <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} className="mt-1" />
      </div>
      <div>
        <Label>Descripción</Label>
        <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} className="mt-1" rows={3} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Duración (min)</Label>
          <Input type="number" value={form.duration} onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))} className="mt-1" />
        </div>
        <div>
          <Label>Precio (€)</Label>
          <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))} className="mt-1" />
        </div>
        <div>
          <Label>Precio Premium (€)</Label>
          <Input type="number" step="0.01" value={form.price_premium} onChange={(e) => setForm(prev => ({ ...prev, price_premium: e.target.value }))} className="mt-1" placeholder="Opcional" />
        </div>
      </div>
      <div>
        <Label>Nota de precio</Label>
        <Input value={form.price_note} onChange={(e) => setForm(prev => ({ ...prev, price_note: e.target.value }))} className="mt-1" placeholder="Ej: Entre semana: 38€ · Premium: 42€" />
      </div>
      <div>
        <Label>Categoría</Label>
        <Input value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))} className="mt-1" />
      </div>
      <div>
        <Label>URL de imagen</Label>
        <Input value={form.image_url} onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))} className="mt-1" placeholder="https://..." />
        {form.image_url && (
          <img src={form.image_url} alt="Preview" className="mt-2 h-24 w-auto rounded-lg object-cover" />
        )}
      </div>
      <div>
        <Label>Beneficios (separados por coma)</Label>
        <Input value={form.benefits} onChange={(e) => setForm(prev => ({ ...prev, benefits: e.target.value }))} className="mt-1" placeholder="Beneficio 1, Beneficio 2, ..." />
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="flex-1 bg-halo-brown hover:bg-halo-charcoal">Guardar</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
};

const ServiceCreateForm = ({ onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    duration: '45',
    price: '',
    price_premium: '',
    price_note: '',
    category: 'individual',
    image_url: '',
    benefits: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.price) {
      return;
    }
    const data = {
      name: form.name,
      description: form.description,
      duration: parseInt(form.duration),
      price: parseFloat(form.price),
      category: form.category,
      image_url: form.image_url,
      benefits: form.benefits.split(',').map(b => b.trim()).filter(b => b)
    };
    if (form.price_premium) data.price_premium = parseFloat(form.price_premium);
    if (form.price_note) data.price_note = form.price_note;
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-service-form">
      <div>
        <Label>Nombre *</Label>
        <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} className="mt-1" required placeholder="Ej: Sesión individual" />
      </div>
      <div>
        <Label>Descripción</Label>
        <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} className="mt-1" rows={3} placeholder="Descripción del servicio..." />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Duración (min)</Label>
          <Input type="number" value={form.duration} onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))} className="mt-1" />
        </div>
        <div>
          <Label>Precio (€) *</Label>
          <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))} className="mt-1" required placeholder="38.00" />
        </div>
        <div>
          <Label>Precio Premium (€)</Label>
          <Input type="number" step="0.01" value={form.price_premium} onChange={(e) => setForm(prev => ({ ...prev, price_premium: e.target.value }))} className="mt-1" placeholder="Opcional" />
        </div>
      </div>
      <div>
        <Label>Nota de precio</Label>
        <Input value={form.price_note} onChange={(e) => setForm(prev => ({ ...prev, price_note: e.target.value }))} className="mt-1" placeholder="Ej: Entre semana: 38€ · Premium: 42€" />
      </div>
      <div>
        <Label>Categoría</Label>
        <select value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="individual">Individual</option>
          <option value="grupo">Grupo</option>
          <option value="familia">Familia</option>
          <option value="especial">Especial</option>
        </select>
      </div>
      <div>
        <Label>URL de imagen</Label>
        <Input value={form.image_url} onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))} className="mt-1" placeholder="https://..." />
        {form.image_url && (
          <img src={form.image_url} alt="Preview" className="mt-2 h-24 w-auto rounded-lg object-cover" />
        )}
      </div>
      <div>
        <Label>Beneficios (separados por coma)</Label>
        <Input value={form.benefits} onChange={(e) => setForm(prev => ({ ...prev, benefits: e.target.value }))} className="mt-1" placeholder="Beneficio 1, Beneficio 2, ..." />
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="flex-1 bg-halo-brown hover:bg-halo-charcoal" data-testid="create-service-submit">Crear servicio</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
};


// Content edit form component
const ContentEditForm = ({ content, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: content.title || '',
    subtitle: content.subtitle || '',
    text: content.text || '',
    image_url: content.image_url || '',
    button_text: content.button_text || '',
    button_link: content.button_link || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {};
    if (form.title) data.title = form.title;
    if (form.subtitle) data.subtitle = form.subtitle;
    if (form.text) data.text = form.text;
    if (form.image_url) data.image_url = form.image_url;
    if (form.button_text) data.button_text = form.button_text;
    if (form.button_link) data.button_link = form.button_link;
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Título</Label>
        <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))} className="mt-1" />
      </div>
      <div>
        <Label>Subtítulo</Label>
        <Input value={form.subtitle} onChange={(e) => setForm(prev => ({ ...prev, subtitle: e.target.value }))} className="mt-1" />
      </div>
      <div>
        <Label>Texto</Label>
        <Textarea value={form.text} onChange={(e) => setForm(prev => ({ ...prev, text: e.target.value }))} className="mt-1" rows={4} />
      </div>
      <div>
        <Label>URL de imagen</Label>
        <Input value={form.image_url} onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))} className="mt-1" placeholder="https://..." />
        {form.image_url && (
          <img src={form.image_url} alt="Preview" className="mt-2 h-20 w-auto rounded-lg object-cover" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Texto del botón</Label>
          <Input value={form.button_text} onChange={(e) => setForm(prev => ({ ...prev, button_text: e.target.value }))} className="mt-1" />
        </div>
        <div>
          <Label>Link del botón</Label>
          <Input value={form.button_link} onChange={(e) => setForm(prev => ({ ...prev, button_link: e.target.value }))} className="mt-1" placeholder="/reservas" />
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="flex-1 bg-halo-brown hover:bg-halo-charcoal">Guardar</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
};

export default AdminPage;
